import { useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { X, CalendarPlus, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { AREA_LABELS, heatmapColor, heatmapStroke } from "@/components/BodyMap";
import {
  CHART_TOOLTIP_STYLE, CHART_GRID_STROKE, AXIS_COLOR, PAIN_DOMAIN,
} from "@/lib/uiConstants";

function correlationBadge(r, confidence) {
  if (r == null || confidence === "weak") return null;
  if (r <= -0.3) return { Icon: TrendingDown, tone: "good", label: `${r}`, hint: "pain drops with stretching" };
  if (r >= 0.3)  return { Icon: TrendingUp,   tone: "bad",  label: `+${r}`, hint: "pain rises with stretching" };
  return { Icon: Minus, tone: "flat", label: `${r}`, hint: "no clear relationship" };
}
const TONE = {
  good: "#166534",
  bad:  "#991b1b",
  flat: "#52525b",
};

/**
 * Floating callout that appears above the clicked pain point. Its backing
 * shape is a large amber-tinted circle whose exact color reflects the pain
 * intensity of the region the user tapped — the graph, stats and CTA sit on
 * top of that circle so the manager immediately sees WHICH point they're
 * inspecting without needing a separate legend or panel.
 */
export default function ClinicBodyPartImpactPopup({
  open,
  area,          // e.g. "shoulder_left" (used only for labelling)
  baseArea,      // e.g. "shoulder"       (key in byBodyPart)
  avg,           // clicked region's avg pain — drives the background tint
  reports,
  byBodyPart,
  onClose,
  onQuickPlan,
}) {
  const stats = baseArea ? byBodyPart?.[baseArea] : null;
  const label = AREA_LABELS?.[area] || (baseArea || area || "").replace(/_/g, " ");
  const bg = heatmapColor(avg);
  const stroke = heatmapStroke(avg);

  const chartData = useMemo(() => {
    if (!stats) return [];
    return (stats.series || []).map((r) => ({
      date: r.date.slice(5),
      pain: r.pain,
      stretch_min: r.stretch_min,
    }));
  }, [stats]);

  const badge = stats ? correlationBadge(stats.correlation, stats.correlation_confidence) : null;

  if (!open) return null;

  return (
    <div
      className="relative w-full flex justify-center mb-6"
      data-testid="clinic-impact-popup"
    >
      {/* Large amber-tinted backing circle */}
      <div
        aria-hidden="true"
        className="absolute -top-6 left-1/2 -translate-x-1/2 rounded-full pointer-events-none"
        style={{
          width: "min(560px, 92%)",
          height: 420,
          background: `radial-gradient(circle at 50% 40%, ${bg} 0%, ${bg}dd 55%, ${bg}55 85%, transparent 100%)`,
          filter: "blur(2px)",
        }}
      />

      {/* Actual card content */}
      <div
        className="relative bg-white/85 backdrop-blur-md border-2 rounded-3xl shadow-2xl w-full max-w-xl mx-auto"
        style={{ borderColor: stroke, boxShadow: `0 20px 40px ${stroke}33` }}
      >
        <div className="flex items-start justify-between px-5 pt-4 pb-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-bold" style={{ color: stroke }}>
              Focused pain point
            </div>
            <h3 className="text-xl font-black mt-0.5 capitalize" style={{ color: stroke }}>
              {label}
            </h3>
            {avg != null && (
              <div className="mt-1 text-xs text-zinc-700">
                <span className="font-bold">{avg}</span>
                <span className="text-zinc-500"> avg pain · {reports} {reports === 1 ? "log" : "logs"} · last 30 days</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close focused region"
            data-testid="clinic-impact-popup-close"
            className="text-zinc-500 hover:text-zinc-900 rounded-full p-1 hover:bg-white/60 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {stats && (chartData.length > 0) ? (
          <>
            {badge && (
              <div
                className="mx-5 mt-1 mb-2 inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 self-start"
                style={{ background: "#ffffffcc", color: TONE[badge.tone] }}
                data-testid="clinic-impact-popup-corr"
              >
                <badge.Icon className="w-3.5 h-3.5" />
                r = {badge.label} · {badge.hint}
              </div>
            )}
            <div className="px-3">
              <ResponsiveContainer width="100%" height={170}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={CHART_GRID_STROKE} strokeOpacity={0.4} />
                  <XAxis dataKey="date" stroke={AXIS_COLOR} fontSize={9} interval="preserveStartEnd" />
                  <YAxis yAxisId="pain" domain={PAIN_DOMAIN} stroke={AXIS_COLOR} fontSize={9} width={22} />
                  <YAxis yAxisId="stretch" orientation="right" stroke={stroke} fontSize={9} width={22} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  <Bar yAxisId="stretch" dataKey="stretch_min" name="Stretching (min)" fill={stroke} opacity={0.35} radius={[3, 3, 0, 0]} />
                  <Line yAxisId="pain" type="monotone" dataKey="pain" name="Pain" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 2, fill: "#0f172a" }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-zinc-600 px-5 pt-1">
              Black line = daily pain · amber bars = minutes stretched this region.
            </p>
          </>
        ) : (
          <div className="px-5 py-6 text-sm text-zinc-700 text-center">
            No stretching / pain history yet for <span className="font-semibold">{label}</span>.
            Schedule a plan below to start collecting data.
          </div>
        )}

        <div className="px-5 py-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="h-9 px-3 rounded-md text-xs font-semibold text-zinc-700 hover:bg-white/70"
            data-testid="clinic-impact-popup-dismiss"
          >
            Dismiss
          </button>
          <button
            onClick={() => onQuickPlan?.(baseArea, area)}
            className="h-9 px-4 rounded-md text-xs font-bold text-white inline-flex items-center gap-1.5 shadow"
            style={{ background: stroke }}
            data-testid={`clinic-impact-popup-quick-plan-${baseArea || area}`}
          >
            <CalendarPlus className="w-3.5 h-3.5" />
            Quick insert stretch plan
          </button>
        </div>
      </div>
    </div>
  );
}
